import { genderOptions } from '@common/enums'
import { CClient } from '@common/utils'
import FloatingLabelInput from '@components/common/floating-input'
import SelectInput from '@components/common/forms/select'
import Modal from '@components/common/modal'
import Spinner from '@components/common/spinner'
import {
  validateSchoolData,
  validateStudentDetailsData,
} from '@containers/book-trial-class/validators'
import { UserProps, useSessionContext } from '@contexts/session'
import { StudentProps } from '@lib/types/classroom'
import { SYSTEM_TIMEZONE, formatTimeByTimezone } from '@lib/utils/date.utils'
import { msgT } from '@lib/utils/i18n'
import { isDummyEmail, isPlaceholderPhone } from '@lib/utils/identifier'
import {
  getInitialEmailValue,
  getInitialGenderObj,
} from '@lib/utils/trial-class'
import CustomPhoneInput from '@components/book-trial-class/phone-input'
import { t, Trans } from '@lingui/macro'
import cogoToast from 'cogo-toast'
import Router from 'next/router'
import React, { FormEvent, useEffect, useState } from 'react'
import { AlertTriangle, X } from 'react-feather'
import SelectSchool from '@components/competitions/select-school'
import { updateUserSchool, showErrorAlert } from '@common/utils'
import { childGradeOptions } from '@lib/enums/registration-form'

export type StudentDetailsModalProps = {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  setNudgeForStudentDetails?: React.Dispatch<React.SetStateAction<boolean>>
  startTime?: string
  studentDetailsProp?: StudentProps
  isInClassroom?: boolean
  /** Updates UI state in parent component */
  updateStudentInfoState?: (user: UserProps) => void
  preventReloadOnDetailsUpdate?: boolean
  isMinimal?: boolean
  /**
   * Ask only for what makes this account reachable: name, phone if it is still
   * a placeholder, email, and school. Grade, date of birth, city, parent name
   * and gender are hidden, school stops being required, and gender and city
   * are left out of the payload rather than sent empty.
   *
   * Used where the parent came to fix one thing. They have already booked, or
   * been told their email bounced, and every extra required field is a chance
   * to abandon a form they did not set out to fill in.
   */
  collectIdentifiersOnly?: boolean
  isPaidUser?: boolean
}

// TODO: Add i18n support
const StudentDetailsModal = ({
  isOpen,
  setIsOpen,
  setNudgeForStudentDetails,
  startTime,
  isMinimal,
  studentDetailsProp,
  isInClassroom = false,
  updateStudentInfoState,
  preventReloadOnDetailsUpdate = false,
  collectIdentifiersOnly,
  isPaidUser = false,
}: StudentDetailsModalProps): JSX.Element => {
  const {
    id,
    student: studentDetailsFromContext,
    teacher,
    name: studentNameFromContext,
    gender: studentGenderFromContext,
    email: studentEmailFromContext,
    phone: studentPhoneFromContext,
    voucher: studentVoucherFromContext,
    city: studentCityFromContext,
    timezone,
    country,
    dob: studentDoBFromContext,
    school: studentSchoolFromContext,
    rejectedEmail,
  } = useSessionContext()

  const studentDetails = teacher
    ? studentDetailsProp
    : studentDetailsFromContext
  const name = teacher ? studentDetailsProp?.name : studentNameFromContext
  const gender = teacher ? studentDetailsProp?.gender : studentGenderFromContext
  const userEmail = teacher
    ? studentDetailsProp?.email
    : studentEmailFromContext
  const phone = teacher ? studentDetailsProp?.phone : studentPhoneFromContext
  const studentVoucher = studentVoucherFromContext
  const studentCity = teacher
    ? studentDetailsProp?.city
    : studentCityFromContext
  const dob = teacher ? studentDetailsProp?.dob : studentDoBFromContext
  const userId = teacher ? studentDetailsProp.userId : id
  const school = teacher ? studentDetailsProp?.school : studentSchoolFromContext

  const [checkedTimezone, setCheckedTimezone] = useState(timezone)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [studentName, setStudentName] = useState(name === 'Student' ? '' : name)
  const [studentGender, setStudentGender] = useState(
    getInitialGenderObj(gender)
  )
  const [voucher, setVoucher] = useState(studentVoucher)
  const [city, setCity] = useState(studentCity)
  const [parentName, setParentName] = useState(studentDetails?.parentName)
  const [grade, setGrade] = useState(studentDetails?.grade)

  const [studentDob, setStudentDob] = useState(dob)
  const [schoolId, setSchoolId] = useState<number>(null)
  const [schoolName, setSchoolName] = useState<string>('')

  const [email, setEmail] = useState(getInitialEmailValue(userEmail))
  // Signups that identified themselves with an email carry a placeholder
  // number. This is where we ask for the real one - the only point in the
  // funnel where the parent has committed enough to hand it over.
  const needsRealPhone = isPlaceholderPhone(phone)
  const [studentPhone, setStudentPhone] = useState('')

  // `rejectedEmail` is kept forever as a record of what the parent typed, so
  // it cannot say on its own whether the address is still broken. The dummy
  // address says that, and it goes the moment they give us a working one.
  const needsEmailFix = Boolean(rejectedEmail) && isDummyEmail(userEmail)

  /** Used to populate form fields when user is teacher.
   * States are updated when StudentDetailsProp updates
   */
  const setInitialValues = () => {
    setStudentName(name === 'Student' ? '' : name)
    setStudentGender(getInitialGenderObj(gender))
    setEmail(getInitialEmailValue(userEmail))
    setVoucher(voucher === 'Coupon code' ? '' : voucher)
    setCity(city === 'City' ? '' : city)
    setParentName(parentName === 'Parent Name' ? '' : parentName)
  }

  type FormDataType = {
    name: string
    gender: string
    email?: string
    // Only sent when this modal is the step collecting it - a signup that
    // arrived by phone already has one.
    phone?: string
    voucher: string
    // Not sent from the post-booking modal, which does not show the field.
    city?: string
    student: {
      grade: string
      parentName: string
      id: number
    }
    dob?: string
  }
  // Errors render next to their field, inside the modal's scrolling body - so
  // on a phone a failure below the fold looks like the Save button simply did
  // nothing. Bring the first one into view and put the caret in it.
  const focusFirstError = (fieldErrors: Record<string, unknown>) => {
    const firstInvalid = ['name', 'phone', 'email', 'school'].find(
      (field) => fieldErrors[field]
    )
    if (!firstInvalid) return
    const input = document.getElementById(firstInvalid)
    input?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    input?.focus({ preventScroll: true })
  }

  const handleUpdateButton = async () => {
    // School is optional when we are only after the identifiers - the parent
    // came to fix one thing, and every required field is a chance to abandon
    // a form they did not set out to fill in.
    const schoolErrors =
      collectIdentifiersOnly && !schoolName
        ? {}
        : { ...validateSchoolData(schoolName) }
    if (Object.keys(schoolErrors).length) {
      setErrors(schoolErrors)
      setLoading(false)
      focusFirstError(schoolErrors)
      return
    }

    const formData: FormDataType = {
      name: studentName,
      gender: collectIdentifiersOnly ? null : studentGender.value,
      email: email,
      ...(needsRealPhone ? { phone: `+${studentPhone}` } : {}),
      voucher: voucher,
      ...(collectIdentifiersOnly ? {} : { city }),
      student: {
        grade: grade,
        parentName: parentName,
        id: studentDetails.id,
      },
      dob: studentDob,
    }

    // Enhanced conversion setup
    window?.gtag?.('set', 'user_data', {
      ['email']: email,
      ['address.first_name']: formData?.name,
      ['phone_number']: needsRealPhone ? `+${studentPhone}` : phone,
      ['address.country']: country,
    })

    // Awaited, and gated on success: this creates a school row and links the
    // user to it, so firing it beside the PATCH meant a rejected save still
    // wrote one - and every retry wrote another. Blur-saving used to mask that
    // by writing the school first anyway; it is now the only path.
    const saved = await updateStudentData(formData)
    if (saved) {
      updateUserSchool(userId, schoolId, schoolName)
    }
  }

  const handleStudentNameChange = (elem: FormEvent<HTMLInputElement>): void => {
    const value: string = elem.currentTarget.value

    setStudentName(value)
  }
  // const handleVoucherChange = (elem: FormEvent<HTMLInputElement>): void => {
  //   const value: string = elem.currentTarget.value
  //   setVoucher(value)
  // }

  const handleCityChange = (elem: FormEvent<HTMLInputElement>): void => {
    const value: string = elem.currentTarget.value
    setCity(value)
  }

  const handleParentName = (elem: FormEvent<HTMLInputElement>): void => {
    const value: string = elem.currentTarget.value
    setParentName(value)
  }

  const handleEmailChange = (elem: FormEvent<HTMLInputElement>): void => {
    const value: string = elem.currentTarget.value

    setEmail(value)
  }

  const handleStudentGenderChange = (
    id: string,
    value: {
      value: string
      label: string
    }
  ) => {
    setStudentGender(value)
    updateStudentDataOnBlur({ gender: value.value })
  }

  const handleStudentGradeChange = (
    id: string,
    value: {
      value: string
      label: string
    }
  ) => {
    if (isPaidUser && value.value === 'Kindergarten') {
      showErrorAlert({
        text:
          'Cannot select Kindergarten grade. Please choose a different grade.',
      })
      return
    }
    setGrade(value.value)
    updateStudentDataOnBlur({ grade: value.value })
  }

  const handleDobChange = (elem: FormEvent<HTMLInputElement>): void => {
    const value: string = elem.currentTarget.value
    setStudentDob(value)
  }

  /**
   * Final update of student details on CTA "update" click.
   * @param formData
   * @returns
   */
  const updateStudentData = async (formData: FormDataType) => {
    const url = `/api/v1/users/${
      teacher ? (studentDetails as StudentProps).userId : 'me'
    }/`
    if (loading) return
    setLoading(true)

    if (!studentDetails?.id) {
      throw new Error('Student ID can not be null')
    }

    const errors = validateStudentDetailsData(formData, collectIdentifiersOnly)
    if (Object.keys(errors).length) {
      setErrors(errors)
      setLoading(false)
      focusFirstError(errors)
      return false
    }

    let saved = false
    try {
      const response = await CClient(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        saved = true
        setIsOpen(false)
        setLoading(false)
        setErrors({})
        setNudgeForStudentDetails && setNudgeForStudentDetails(false)
        if (updateStudentInfoState) {
          const user = await response.json()
          updateStudentInfoState(user)
        }
        cogoToast
          .success(
            `${
              teacher
                ? t({
                    id: 'trialClass.studentDetailsModal.successful.teacher',
                    message: "Student's details have been saved successfully.",
                  })
                : t({
                    id: 'trialClass.studentDetailsModal.successful.student',
                    message:
                      'Your personal details have been saved successfully.',
                  })
            }`
          )
          .then(() => !preventReloadOnDetailsUpdate && Router.reload())
      } else {
        const error = await response.json()
        setLoading(false)
        setErrors(error)
      }
    } catch (error) {
      console.error(error)
    }
    return saved
  }

  /**
   * Update student data on input blur.
   * @param formData
   */
  const updateStudentDataOnBlur = async (formData) => {
    if (isPaidUser) {
      return
    }

    const url = `/api/v1/users/${
      teacher ? (studentDetails as StudentProps).userId : 'me'
    }/`

    if (!studentDetails?.id) {
      throw new Error('Student ID can not be null')
    }

    try {
      const response = await CClient(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (!response.ok) {
        const error = await response.json()
        console.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (!checkedTimezone) setCheckedTimezone(SYSTEM_TIMEZONE)
  }, [])

  useEffect(() => {
    teacher && setInitialValues()
  }, [studentDetailsProp])

  useEffect(() => {
    if (school) {
      setSchoolId(school?.id)
      setSchoolName(school?.title)
    }
  }, [])

  // Rendered in one order or the other below, and defined once so a
  // change to either field cannot be made to only one copy.
  const emailField = (
    <div className="mt-4">
      {/* Above the field, not below it, and on a background rather than
                in grey. Below it reads as a note about the value now in the
                box - which is the suggested correction, not the problem - and
                grey is the same treatment as the neutral hint on the phone
                field, so nothing marks it as something to act on. Red is
                taken: this form uses it for "what you just typed is wrong". */}
      {needsEmailFix && (
        <div
          role="alert"
          className="flex p-3 mb-2 text-sm rounded bg-yellow-100 text-grey"
        >
          {/* `flex-shrink-0` or the icon squashes as the text wraps to a
              second line, which it does at phone width. Decorative: the
              sentence beside it already says what is wrong. */}
          <AlertTriangle
            size={16}
            className="flex-shrink-0 mt-1 mr-2 text-orange2-400"
            aria-hidden="true"
          />
          <span>
            <Trans id="trialClass.studentDetailsModal.rejectedEmail">
              <span className="font-600">{rejectedEmail}</span> is not a working
              email address, so we could not reach you. Please correct it below.
            </Trans>
          </span>
        </div>
      )}
      <FloatingLabelInput
        label={t({
          id: 'trialClass.studentDetailsModal.fieldLabels.email',
          message: 'Email',
        })}
        id="email"
        name="email"
        type="email"
        value={email}
        onChange={handleEmailChange}
        errorText={errors['email']}
        required
      />
    </div>
  )

  const phoneField = needsRealPhone && (
    <div className="mt-4">
      <CustomPhoneInput
        label={t({
          id: 'trialClass.studentDetailsModal.fieldLabels.phone',
          message: 'Phone number',
        })}
        helpText={t({
          id: 'trialClass.studentDetailsModal.helpText.phone',
          message: `For class reminders and if your teacher needs to reach you.`,
        })}
        id="phone"
        name="phone"
        // Every other field on this form arrives prefilled, so nothing
        // distinguishes the one we actually need. "Required" would not
        // say it - Name and Email are required too.
        labelBadge={t({
          id: 'trialClass.studentDetailsModal.phoneBadge',
          message: 'Important',
        })}
        // Its copy offers to let you "register" with another number -
        // written for the signup form, wrong on a screen shown after
        // the parent has registered and booked. The helper text below
        // gives the reason better anyway.
        hideWhatsAppTooltip
        value={studentPhone}
        onChange={(value: string) => setStudentPhone(value)}
        errorList={errors['phone']}
        required
      />
    </div>
  )

  return (
    <Modal isOpen={isOpen} width="max-w-xl" setIsOpen={setIsOpen}>
      <>
        <div className="flex justify-end relative">
          {/* `z-10` is load-bearing. The phone field renders a Tooltip, and
              react-laag anchors its layer by setting `position: relative` on
              the nearest scroll container - the `overflow-auto` div below.
              That div then paints above this absolutely-positioned button,
              which shares its `z-index: auto`, and swallows the click. */}
          <button
            type="button"
            // -m-2 keeps the icon where it was while the padding grows the hit
            // area to 44px; the bare icon was a 24px target.
            className="focus:outline-none absolute z-10 p-2 -m-2"
            aria-label={t({
              id: 'trialClass.studentDetailsModal.closeButton',
              message: 'Close',
            })}
            onClick={() => setIsOpen(false)}
          >
            <X className="stroke-1 text-grey-800" />
          </button>
        </div>
        <div className="px-5 overflow-auto">
          {/* Condition for header content on modal displayed to teacher everywhere */}
          {teacher && (
            <h2 className="font-600">
              <Trans id="trialClass.studentDetailsModal.heading.teacher">
                Update student details
              </Trans>
            </h2>
          )}

          {/* Condition for header content on student dashboard */}
          {isMinimal && !teacher && (
            // `modal.tsx` sets aria-labelledby="modal-headline" on the dialog.
            // The booking heading below carries the id for the callers that
            // pass a start time; this is the id for the ones that do not.
            // Safe because no caller passes both `isMinimal` and `startTime`.
            <h2 id="modal-headline" className="font-600 text-xl">
              <Trans id="trialClass.studentDetailsModal.heading.student.minimal">
                Update your info to get a personalised experience at Codingal.
              </Trans>
            </h2>
          )}

          {/* Condition for header content inside classroom for student */}
          {!isMinimal && !teacher && isInClassroom && (
            <div className="flex flex-col space-y-5 leading-tight">
              <h2 className="text-2xl font-600">
                <Trans id="trialClass.studentDetailsModal.heading.student">
                  Welcome to the magical world of Codingal.
                </Trans>
              </h2>
              <p>
                <Trans id="trialClass.studentDetailsModal.subHeading.student">
                  We would like to know more about you to provide a great
                  experience in the trial class. This information is saved in a
                  secure way.
                </Trans>
              </p>
              <p className="font-600">
                <Trans id="trialClass.studentDetailsModal.subHeading.student.2">
                  Update your details
                </Trans>
              </p>
            </div>
          )}

          {/* Only where a class has actually been booked. The same trimmed
              form is used to repair an unreachable account, which has no
              start time and may have no booking at all - and a heading about
              a class is noise to a parent who came to fix their email. */}
          {collectIdentifiersOnly && startTime && (
            <>
              {/* `modal.tsx` sets aria-labelledby="modal-headline" on the
                  dialog; without this id the dialog has no accessible name. */}
              <p
                id="modal-headline"
                className="text-grey text-xl lg:text-2xl font-500 leading-tight mb-1 mt-4 text-center"
              >
                <Trans id="trialClass.studentDetailsModal.classBookingTime">
                  Your class is booked at <br />
                  <span className="font-600">
                    {formatTimeByTimezone(
                      startTime,
                      checkedTimezone,
                      'h:mm A [Z] on ddd, MMM DD'
                    )}
                  </span>
                </Trans>
              </p>
              {/* Ties the form to the outcome without claiming it grants
                  eligibility. Per the public FAQ (`lib/data/courses-faq.tsx`) a
                  scholarship is agreed with an Academic Counselor after the
                  trial, so "fill this in to become eligible" was a promise we
                  do not keep - but the details genuinely gate the conversation,
                  because without a number the counselor cannot reach them. */}
              <p className="text-sm lg:text-base leading-tight mt-3 mb-6 text-center font-400">
                <Trans id="trialClass.studentDetailsModal.subHeading.student.3">
                  Add your details so your Academic Counselor can discuss{' '}
                  <span className="font-600 whitespace-no-wrap">
                    up to 50% scholarship
                  </span>{' '}
                  after your trial class.
                </Trans>
              </p>
            </>
          )}

          <div className="mt-4">
            <FloatingLabelInput
              label={t({
                id: 'trialClass.studentDetailsModal.fieldLabels.name',
                message: 'Name',
              })}
              id="name"
              name="name"
              value={studentName}
              onChange={handleStudentNameChange}
              required
              errorText={errors['name']}
              // No autoFocus: Name arrives prefilled, so the caret landed
              // somewhere with nothing to do while the mobile keyboard covered
              // the booking confirmation.
            />
          </div>
          {/* Name is always first; then whichever identifier this parent
              actually came to fix, so it is the field they see without
              scrolling. From the rejected-email bar that is the email -
              the bar told them their address failed, and leading with a
              phone field marked Important contradicts it. From the join
              page it is the phone, which is what the class needs. Both
              are missing for an email signup whose address then bounced. */}
          {needsEmailFix ? (
            <>
              {emailField}
              {phoneField}
            </>
          ) : (
            <>
              {phoneField}
              {emailField}
            </>
          )}
          <div className="mt-4">
            <SelectSchool
              setSchoolName={setSchoolName}
              setSchoolId={setSchoolId}
              schoolName={schoolName}
              errorText={errors['school']}
              // Last field in a modal whose dialog is overflow-hidden and whose
              // body is overflow-auto, so a downward list is clipped away
              // entirely no matter its z-index.
              openSuggestionsUpward
            />
          </div>
          {/* Do not display grade abd date of birth in the join trial class page */}
          {!collectIdentifiersOnly && (
            <>
              <div className="mt-4">
                <SelectInput
                  id="childGrade"
                  placeholder="Grade"
                  value={{
                    value: grade,
                    label: msgT(grade),
                  }}
                  handleSelectChange={handleStudentGradeChange}
                  isSearchable={false}
                  isInModal={true}
                  options={childGradeOptions}
                  errorText={errors['grade']}
                  required
                />
              </div>
              <div className="mt-4 flex items-center">
                <span className="text-grey mr-5">Date of Birth: </span>
                <FloatingLabelInput
                  id="birthday"
                  name="birthday"
                  type="date"
                  value={studentDob}
                  onChange={handleDobChange}
                />
              </div>
            </>
          )}
          {/* Not on the post-booking modal: the class is already booked and
              every extra field is a chance to abandon the form. */}
          {!collectIdentifiersOnly && (
            <div className="mt-4">
              <FloatingLabelInput
                label={t({
                  id: 'trialClass.studentDetailsModal.fieldLabels.city',
                  message: 'City',
                })}
                id="city"
                name="city"
                value={city}
                onChange={handleCityChange}
                errorText={errors['city']}
              />
            </div>
          )}
          {/* Donot display parent name & gender in the join trial class page */}
          {!collectIdentifiersOnly && (
            <>
              <div className="mt-4">
                <FloatingLabelInput
                  label={t({
                    id: 'trialClass.studentDetailsModal.fieldLabels.parentName',
                    message: 'Parent Name',
                  })}
                  id="parentName"
                  name="parentName"
                  value={parentName}
                  onChange={handleParentName}
                  errorText={errors['parentName']}
                />
              </div>

              <div className="mt-4">
                <SelectInput
                  id="childGender"
                  placeholder="Select"
                  value={{
                    value: studentGender.value,
                    label: msgT(studentGender.label),
                  }}
                  handleSelectChange={handleStudentGenderChange}
                  isSearchable={false}
                  isInModal={true}
                  options={genderOptions}
                  errorText={errors['gender']}
                  required
                />
              </div>
            </>
          )}
          {/* {shouldGetVoucherCode && (
            <div className="mt-4">
              <FloatingLabelInput
                label={t({
                  id: 'trialClass.studentDetailsModal.fieldLabels.voucher',
                  message: 'Coupon code',
                })}
                id="voucher"
                name="voucher"
                value={voucher}
                onChange={handleVoucherChange}
                errorText={errors['voucher']}
                toolTipText="Add any voucher/coupon/gift code that you may have received. You will get corresponding discount at the time of purchase."
              />
            </div>
          )} */}

          <div className="flex mt-5 mb-5">
            <button
              onClick={handleUpdateButton}
              className="btn btn-orange font-400 w-full py-3"
              disabled={loading}
            >
              {loading ? (
                <Spinner classes="text-white h-5 w-5" />
              ) : collectIdentifiersOnly ? (
                <Trans id="trialClass.studentDetailsModal.saveCTA">Save</Trans>
              ) : (
                <Trans id="trialClass.studentDetailsModal.updateCTA">
                  Update
                </Trans>
              )}
            </button>
          </div>
        </div>
      </>
    </Modal>
  )
}

export default StudentDetailsModal
